/**
 * ProfilePage — Account tabs: plan, profile, brokers, security, privacy, demo.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import BrokerLinkSection from "@/components/BrokerLinkSection";

const TIER_LABEL = {
  trial: "Trial",
  free: "Free",
  pro: "Pro",
  legacy_pro: "Pro (legacy)",
};
const TIER_COLOR = {
  trial: "#0ea5e9",
  free: "#71717A",
  pro: "#10b981",
  legacy_pro: "#10b981",
};

const TAB_IDS = ["plan", "profile", "brokers", "security", "privacy", "demo"];

export default function ProfilePage() {
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [planPriceLabel, setPlanPriceLabel] = useState(null);
  const [params, setParams] = useSearchParams();
  const tabParam = (params.get("tab") || "plan").toLowerCase();
  const activeTab = TAB_IDS.includes(tabParam) ? tabParam : "plan";

  useEffect(() => {
    api.get("/profile/me")
      .then(({ data }) => setProfile(data))
      .catch((err) => setError(err?.response?.data?.detail || "Failed to load profile"));
    api.get("/billing/me")
      .then(({ data }) => {
        const usd = data?.price_usd;
        if (usd != null && Number(usd) > 0) {
          setPlanPriceLabel(`SUBSCRIBE — $${Number(usd).toFixed(0)} USD/MO →`);
        } else {
          setPlanPriceLabel("SUBSCRIBE →");
        }
      })
      .catch(() => setPlanPriceLabel("SUBSCRIBE →"));
  }, []);

  const handleUpgrade = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        origin: window.location.origin,
        package: "markovlab_monthly",
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not start checkout");
    } finally {
      setBusy(false);
    }
  };

  const handleManageSub = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal");
      if (data?.portal_url) {
        window.location.href = data.portal_url;
      } else if (data?.note) {
        alert(data.note);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Portal unavailable");
    } finally {
      setBusy(false);
    }
  };

  if (error) return <div className="p-6 text-sm text-red-400" data-testid="profile-error">{error}</div>;
  if (!profile) return <div className="p-6 text-sm opacity-60">Loading profile…</div>;

  const tier = profile.tier || "free";
  const isTrial = tier === "trial";
  const isPro = tier === "pro" || tier === "legacy_pro";
  const daysLeft = profile.trial_days_remaining;

  return (
    <div className="px-6 py-8 max-w-3xl mx-auto space-y-6" data-testid="profile-page">
      <header>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Account</h1>
        <div className="text-[11px] opacity-60 mt-1">{profile.email}</div>
      </header>

      <Tabs value={activeTab} onValueChange={(v) => setParams({ tab: v }, { replace: true })}>
        <TabsList className="h-8 rounded-sm bg-[var(--hoip-hover)] p-0.5 flex flex-wrap">
          <TabsTrigger value="plan" className="text-xs h-7 rounded-sm px-3" data-testid="account-tab-plan">Current Plan</TabsTrigger>
          <TabsTrigger value="profile" className="text-xs h-7 rounded-sm px-3" data-testid="account-tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="brokers" className="text-xs h-7 rounded-sm px-3" data-testid="account-tab-brokers">Brokers</TabsTrigger>
          <TabsTrigger value="security" className="text-xs h-7 rounded-sm px-3" data-testid="account-tab-security">Security</TabsTrigger>
          <TabsTrigger value="privacy" className="text-xs h-7 rounded-sm px-3" data-testid="account-tab-privacy">Data &amp; Privacy</TabsTrigger>
          <TabsTrigger value="demo" className="text-xs h-7 rounded-sm px-3" data-testid="account-tab-demo">Sample Portfolio</TabsTrigger>
        </TabsList>

      <TabsContent value="plan" className="mt-4 space-y-4">
      {/* Tier card */}
      <section className="border p-4 rounded-sm space-y-3"
        style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
        data-testid="profile-tier-card">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-[10px] tracking-widest opacity-60">CURRENT PLAN</div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-2xl font-bold" style={{ color: TIER_COLOR[tier] }}
                data-testid="profile-tier">
                {TIER_LABEL[tier] || tier}
              </span>
              {isTrial && daysLeft != null && (
                <span className="text-[12px] opacity-70" data-testid="profile-trial-days">
                  · {daysLeft} day{daysLeft === 1 ? "" : "s"} remaining
                </span>
              )}
            </div>
          </div>
          {!isPro && (
            <button onClick={handleUpgrade} disabled={busy}
              className="px-3 py-1.5 text-xs font-bold rounded-sm disabled:opacity-50"
              style={{ background: "var(--hoip-text-primary)", color: "var(--hoip-bg)" }}
              data-testid="profile-upgrade-btn">
              {busy ? "Loading…" : (planPriceLabel || "SUBSCRIBE →")}
            </button>
          )}
          {isPro && (
            <button onClick={handleManageSub} disabled={busy}
              className="px-3 py-1.5 text-xs font-bold rounded-sm border disabled:opacity-50"
              style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}
              data-testid="profile-manage-sub-btn">
              {busy ? "Loading…" : "Manage subscription"}
            </button>
          )}
        </div>
        {isTrial && (
          <p className="text-[12px] opacity-75">
            Your trial ends on {profile.trial_expires_at ? new Date(profile.trial_expires_at).toLocaleDateString() : "day 15"}.
            After expiry, continuing to use MarkovLab requires a paid plan. Broker connections stay available
            on every account. Each email is eligible for one 15-day trial.
          </p>
        )}
        {!isPro && !isTrial && (
          <p className="text-[12px] opacity-75">
            You are on the <strong>Free demo</strong> tier. Broker connections (Tradier / Alpaca) are available
            on this account. Subscribe to keep the full workflow after the trial window.
          </p>
        )}
        {isPro && profile.pro_since && (
          <p className="text-[12px] opacity-75">
            Pro since {new Date(profile.pro_since).toLocaleDateString()}.
          </p>
        )}
      </section>
      </TabsContent>

      <TabsContent value="profile" className="mt-4">
      <ProfileDetailsCard profile={profile} onSaved={(name) => setProfile({ ...profile, display_name: name })} />
      </TabsContent>

      <TabsContent value="brokers" className="mt-4">
        <BrokerLinkSection />
      </TabsContent>

      <TabsContent value="security" className="mt-4">
      <section className="border p-4 rounded-sm space-y-2"
        style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
        data-testid="change-password-card">
        <div className="text-[10px] tracking-widest opacity-60">ACCOUNT SECURITY</div>
        <p className="text-[12px] opacity-75">
          Password and sign-in methods are managed in your Clerk account menu (avatar, top right).
        </p>
      </section>
      </TabsContent>

      <TabsContent value="privacy" className="mt-4 space-y-4">
      <DataPrivacyCard email={profile.email} />
      <section className="border p-4 rounded-sm"
        style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
        <div className="text-[10px] tracking-widest opacity-60 mb-2">LEGAL</div>
        <ul className="text-[12px] space-y-1">
          <li><a href="/legal/terms" className="underline opacity-80 hover:opacity-100">Terms of Use</a></li>
          <li><a href="/legal/risk" className="underline opacity-80 hover:opacity-100">Risk Disclosure</a></li>
          <li><a href="/legal/privacy" className="underline opacity-80 hover:opacity-100">Privacy Policy</a></li>
        </ul>
      </section>
      </TabsContent>

      <TabsContent value="demo" className="mt-4">
      <DemoDataCard />
      </TabsContent>
      </Tabs>
    </div>
  );
}

function ProfileDetailsCard({ profile, onSaved }) {
  const [name, setName] = useState(profile.display_name || "");
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setMsg(null); setErr(null); setBusy(true);
    try {
      await api.patch("/profile/me", { display_name: name.trim() });
      setMsg("Saved.");
      onSaved && onSaved(name.trim());
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to save.");
    } finally { setBusy(false); }
  };

  return (
    <section className="border p-4 rounded-sm space-y-3"
      style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
      data-testid="profile-details-card">
      <div className="text-[10px] tracking-widest opacity-60">PROFILE</div>
      <div className="max-w-sm">
        <label className="text-[11px] opacity-70 block mb-0.5">Display name</label>
        <div className="flex gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80}
            data-testid="profile-display-name-input"
            className="flex-1 h-8 px-2 text-[12px] rounded-sm border bg-[#0A0A0A] border-[#27272A] text-[#F4F4F5]" />
          <button onClick={save} disabled={busy || name.trim() === (profile.display_name || "")}
            className="px-3 h-8 text-xs font-bold rounded-sm disabled:opacity-40"
            style={{ background: "var(--hoip-text-primary)", color: "var(--hoip-bg)" }}
            data-testid="profile-display-name-save">
            {busy ? "…" : "Save"}
          </button>
        </div>
        <p className="text-[10px] opacity-50 mt-1">
          An alias is fine — we don't need your real name, and we don't ask for your country.
        </p>
        {msg && <div className="text-[11px] text-emerald-400" data-testid="profile-name-saved">{msg}</div>}
        {err && <div className="text-[11px] text-red-400">{err}</div>}
      </div>
      <dl className="text-[12px] grid grid-cols-2 gap-y-1 max-w-sm">
        <dt className="opacity-60">Terms accepted</dt>
        <dd>
          v{profile.terms_version_accepted || "—"}
          {profile.terms_accepted_at && (
            <span className="opacity-50"> · {new Date(profile.terms_accepted_at).toLocaleDateString()}</span>
          )}
        </dd>
      </dl>
      {profile.consent_history?.length > 0 && (
        <div className="max-w-sm" data-testid="consent-history">
          <div className="text-[10px] opacity-50 mb-1">Consent history</div>
          <ul className="space-y-0.5 text-[11px] font-mono-data opacity-70">
            {profile.consent_history.map((c, i) => (
              <li key={i} className="flex justify-between">
                <span>v{c.terms_version}</span>
                <span>{new Date(c.accepted_at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function DataPrivacyCard({ email }) {
  const [delConfirm, setDelConfirm] = useState("");
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);

  const download = async (path, filename) => {
    setBusy(path); setErr(null);
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr("Download failed — please try again.");
    } finally { setBusy(null); }
  };

  const deleteAccount = async () => {
    if (delConfirm !== "DELETE") return;
    const confirmMsg = "This permanently deletes your account and all personal data. Your trade history is retained only in anonymized form, not associated with your account. Continue?";
    if (!window.confirm(confirmMsg)) return;
    setBusy("delete"); setErr(null);
    try {
      await api.post("/profile/delete-account", { confirm: delConfirm });
      window.location.href = "/";
    } catch (e) {
      setErr(e?.response?.data?.detail || "Deletion failed.");
      setBusy(null);
    }
  };

  return (
    <section className="border p-4 rounded-sm space-y-4"
      style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
      data-testid="data-privacy-card">
      <div className="text-[10px] tracking-widest opacity-60">DATA & PRIVACY (GDPR)</div>
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          <button onClick={() => download("/profile/export", "markovlab-data-export.json")} disabled={!!busy}
            className="px-3 py-1.5 text-xs font-bold rounded-sm border disabled:opacity-50"
            style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}
            data-testid="export-data-btn">
            {busy === "/profile/export" ? "Preparing…" : "Download my data (JSON)"}
          </button>
          <button onClick={() => download("/profile/export/trade-log", "markovlab-trade-log.csv")} disabled={!!busy}
            className="px-3 py-1.5 text-xs font-bold rounded-sm border disabled:opacity-50"
            style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}
            data-testid="export-tradelog-btn">
            {busy === "/profile/export/trade-log" ? "Preparing…" : "Download trade log (CSV)"}
          </button>
        </div>
        <p className="text-[10px] opacity-50">
          Exports include everything tied to {email}: profile, trade log, positions, settings and watchlists.
        </p>
      </div>
      <div className="border-t pt-3 space-y-2" style={{ borderColor: "var(--hoip-border)" }}>
        <div className="text-[11px] font-bold text-red-400">Delete my account</div>
        <p className="text-[10px] opacity-60 max-w-lg">
          Permanently erases your account personal data, including your email address, display name or alias,
          password hash, profile settings, positions, and account-linked app data used to provide and secure your
          account. We do not retain broker account numbers, home addresses, or uploaded filenames as part of your
          profile, and uploaded import files are processed so those identifiers are not persisted. Your trade
          history is retained <b>only in anonymized form</b> for aggregate statistics, with account linkage, notes,
          broker execution IDs, and filenames removed. This anonymized data is not associated with your account and
          this action cannot be undone. Download your data first.
        </p>
        <div className="flex gap-2 items-center max-w-sm">
          <input value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)} placeholder='Type DELETE to confirm'
            data-testid="delete-account-confirm-input"
            className="flex-1 h-8 px-2 text-[12px] rounded-sm border bg-[#0A0A0A] border-[#27272A] text-[#F4F4F5]" />
          <button onClick={deleteAccount} disabled={delConfirm !== "DELETE" || !!busy}
            className="px-3 h-8 text-xs font-bold rounded-sm bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
            data-testid="delete-account-btn">
            {busy === "delete" ? "Deleting…" : "Delete account"}
          </button>
        </div>
        {err && <div className="text-[11px] text-red-400" data-testid="delete-account-error">{err}</div>}
      </div>
    </section>
  );
}

// ─── Demo data (seeded sample portfolio) — explicit type-to-confirm clear ───
function DemoDataCard() {
  const [status, setStatus] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = () => api.get("/profile/demo-data-status").then(({ data }) => setStatus(data)).catch(() => setStatus({ has_demo_data: false }));
  useEffect(() => { load(); }, []);

  if (!status?.has_demo_data) {
    return (
      <section className="border p-4 rounded-sm"
        style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}
        data-testid="demo-data-card-empty">
        <h2 className="text-sm font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Sample portfolio</h2>
        <p className="text-[11px] mt-1" style={{ color: "var(--hoip-text-secondary)" }}>
          No sample portfolio is loaded on this account.
        </p>
      </section>
    );
  }
  const c = status.counts || {};

  const clear = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post("/profile/clear-demo-data", { confirm: confirmText });
      setMsg({ ok: true, text: `Demo data cleared — ${data.deleted?.total ?? 0} records removed.` });
      setConfirmText("");
      load();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || "Failed to clear demo data." });
    } finally { setBusy(false); }
  };

  return (
    <section className="border p-4 rounded-sm space-y-3"
      style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}
      data-testid="demo-data-card">
      <div>
        <h2 className="text-sm font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Sample portfolio (demo data)</h2>
        <p className="text-[11px] mt-1" style={{ color: "var(--hoip-text-secondary)" }}>
          Your account was pre-loaded with a sample portfolio so you could explore MarkovLab immediately:
          {" "}{c.stock_positions || 0} stock positions, {c.options_positions || 0} open options and {c.trade_log || 0} trade-log entries.
          Clearing removes <strong>only</strong> the seeded records — anything you added yourself is untouched.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
          placeholder='Type "CLEAR DEMO" to confirm'
          className="h-7 px-2 text-[11px] font-mono-data border rounded-sm bg-transparent w-56"
          style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}
          data-testid="clear-demo-confirm-input" />
        <button onClick={clear} disabled={busy || confirmText.trim().toUpperCase() !== "CLEAR DEMO"}
          className="h-7 px-3 text-[11px] font-bold rounded-sm border disabled:opacity-40"
          style={{ borderColor: "rgba(239,68,68,0.5)", color: "#EF4444", background: "rgba(239,68,68,0.08)" }}
          data-testid="clear-demo-btn">
          {busy ? "Clearing…" : "Clear demo data"}
        </button>
      </div>
      <p className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>This is irreversible — the sample data cannot be restored afterwards.</p>
      {msg && <div className={`text-[11px] ${msg.ok ? "text-emerald-400" : "text-red-400"}`} data-testid="clear-demo-msg">{msg.text}</div>}
    </section>
  );
}
